import {
  type ImpersonatorSession,
  type UserSession,
  getUserRoles,
} from '@solinkcorp/authentication';
import memoizeOne from 'memoize-one';
import { IS_PRODUCTION } from 'src/environment';
import pkg from '../../../package.json';
import { isKiosk } from '../utils/kioskUtils';

const { version } = pkg;

const ANALYTICS_ON = 'ANALYTICS_ON';

// Note we could implement some kind of tracking event queueing in combination
// with retries, however this would affect the timestamps on tracked events
// (which might not be desirable)
const LOAD_RETRY_TIMEOUT_MS = 200;
const LOAD_RETRY_ATTEMPTS = 15;

// iOS is using this format for the user features sent to Intercom
// so VD needs to keep consistent
// otherwise there will be two different entries in Intercom although the displayed entries looks same
export const USER_FEATURE_KEY_PREFIX = 'Feature - ';

export type AnalyticsPlatform = 'Web' | 'WebConnect' | 'WebVideoWall';
export interface AnalyticsEvent {
  name: string;
  properties?: Record<string, unknown>;
  trackIntercom?: boolean;
  trackMixpanel?: boolean;
  auditEventSubtype?: string;
}

// Memoized result for getTrackingSuperProperties().
let cachedTrackingSuperProperties:
  | {
      user: UserSession;
      impersonator: ImpersonatorSession | undefined;
      superProperties: Record<string, unknown>;
    }
  | undefined;

/**
 * Return a set of tracking properties to include in all events.
 */
export const getTrackingSuperProperties = memoizeOne(
  async (
    user: UserSession,
    impersonator: ImpersonatorSession | undefined,
  ): Promise<Record<string, unknown>> => {
    // Only recompute super properties if the user or impersonator have changed.
    if (
      !cachedTrackingSuperProperties ||
      cachedTrackingSuperProperties?.user !== user ||
      cachedTrackingSuperProperties.impersonator !== impersonator
    ) {
      let userRoleName: string | undefined;
      if (user.type === 'guest') {
        // Guests don't have permission to call getUserRoles().
        userRoleName = 'guest';
      } else {
        const userRoles = await getUserRoles(user);
        userRoleName = userRoles.find((role) => role.id === user.profile.userRoleId)?.name;
      }
      const platform: AnalyticsPlatform = isKiosk() ? 'WebConnect' : 'Web';

      const superProperties: Record<string, unknown> = {
        email: user.jwtUser.email,
        userRole: userRoleName,
        userType: user.type === 'guest' ? 'guest' : user.jwtUser.app_metadata.userType,
        impersonation: !!impersonator,
        impersonatorEmail: impersonator?.jwtUser.email,
        impersonatorAuth0Id: impersonator?.jwtUser.sub,
        cloudId: user.jwtUser.app_metadata.cloudId,
        resellerId: user.jwtUser.app_metadata.resellerId,
        resellerName: user.profile.resellerName,
        customerId: user.customerId,
        customerName: user.profile.customerName,
        appVersion: version,
        auth0Id: user.jwtUser.sub,
        solink: user.isSolinkUser,
        platform,
        awsRegion: user.serviceMap.awsRegion,
      };

      cachedTrackingSuperProperties = { user, impersonator, superProperties };
    }

    return cachedTrackingSuperProperties.superProperties;
  },
);

/** Returns true if we should use mock instances for analytics. */
export function overrideModule(): boolean {
  const forceUseRealAnalytics = localStorage.getItem(ANALYTICS_ON);
  if (forceUseRealAnalytics !== null) {
    return !forceUseRealAnalytics;
  }
  return !IS_PRODUCTION;
}

/**
 * Returns the intercom or mixpanel instance.
 */
export async function loadTracker<T>(
  moduleName: string,
  fn: () => T,
  maxRetries: number = LOAD_RETRY_ATTEMPTS,
): Promise<T> {
  const moduleInstance = await fn();
  if (moduleInstance) {
    console.debug(`Analytics: Load ${moduleName} success`);
    return moduleInstance;
  } else if (maxRetries > 0) {
    return new Promise<T>((resolve, reject) =>
      window.setTimeout(
        () => loadTracker(moduleName, fn, maxRetries - 1).then(resolve, reject),
        LOAD_RETRY_TIMEOUT_MS,
      ),
    );
  }
  throw new Error(`Retry attempts to load ${moduleName} failed`);
}

export function remapUserFeatures(
  features: Record<string, boolean> | undefined,
): Record<string, boolean> {
  const remappedFeatures: Record<string, boolean> = {};
  for (const key of Object.keys(features || {})) {
    remappedFeatures[`${USER_FEATURE_KEY_PREFIX}${key}`] = features?.[key] || false;
  }
  return remappedFeatures;
}
